import type {
  CredentialDialogState,
  CredentialDialog,
} from "@softmax/product-ux/account";
import type { ComponentProps } from "react";

export function credentialDialogState({
  token,
  copied,
  copyError,
  action,
  name,
  allowWrite,
  pending,
  error,
}: {
  token: string | null;
  copied: boolean;
  copyError: string | undefined;
  action: "create" | "revoke";
  name: string;
  allowWrite: boolean | undefined;
  pending: boolean;
  error: string | undefined;
}): CredentialDialogState {
  if (token) {
    let state: CredentialDialogState = { kind: "secret", token, copied };

    if (copyError !== undefined) state = { ...state, copyError: copyError };

    return state;
  }

  let state: Exclude<CredentialDialogState, { kind: "secret" }> =
    action === "revoke"
      ? { kind: "revoke", name, pending }
      : { kind: "create", name, pending };

  if (state.kind === "create" && allowWrite !== undefined)
    state = { ...state, allowWrite: allowWrite };

  if (error !== undefined) state = { ...state, error: error };

  return state;
}

export function credentialFeedback({
  uncertain,
  error,
  refreshError,
  refreshedAt,
  submittedAt,
  refreshing,
}: {
  readonly uncertain: boolean;
  readonly error: Error | null;
  readonly refreshError: Error | null;
  readonly refreshedAt: number;
  readonly submittedAt: number;
  readonly refreshing: boolean;
}): Pick<ComponentProps<typeof CredentialDialog>, "recovery"> & {
  readonly error: string | undefined;
} {
  const message = uncertain
    ? `The result is unknown. Refresh credentials before retrying. ${error?.message}`
    : error?.message;

  if (!uncertain) return { error: message };

  let recovery: NonNullable<
    ComponentProps<typeof CredentialDialog>["recovery"]
  > = { refreshing };

  if (refreshError)
    recovery = {
      ...recovery,
      message: `Refresh failed: ${refreshError.message}`,
    };
  else if (refreshedAt > submittedAt)
    recovery = {
      ...recovery,
      message:
        "Credentials refreshed. Close this dialog and inspect the list before retrying.",
    };

  return { error: message, recovery };
}
